<template>
    <div class="ppv2-flex ppv2-items-center">
        <date-range-picker
            ref="picker"
            v-model="dateRange"
            :locale-data="localeData"
            :auto-apply="true"
            :max-date="formattedMaxDate"
            :min-date="formattedMinDate"
            opens="right"
            @update="handleDateChange"
            :ranges="filteredRanges"
        >
            <template v-slot:input="picker">
                <div
                    class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-text-black ppv2-text-opacity-65 hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-colors ppv2-cursor-pointer"
                >
                    <CalendarCheck :size="17" style="pointer-events: none" />
                    <span class="ppv2-text-black ppv2-text-opacity-65" style="pointer-events: none">
                        {{ formatDateRange(picker.startDate, picker.endDate) }}
                    </span>
                </div>
            </template>
        </date-range-picker>
    </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import moment from 'moment'
import { CalendarCheck } from 'lucide-vue'

export default {
    name: 'InvoiceDateFilter',
    components: { DateRangePicker, CalendarCheck },

    props: {
        startDate: {
            type: String,
            default: () => moment().format('YYYY-MM-DD'),
        },
        endDate: {
            type: String,
            default: () => moment().format('YYYY-MM-DD'),
        },
        maxDate: {
            type: [String, Date, Boolean],
            default: false,
        },
        minDate: {
            type: [String, Date, Boolean],
            default: false,
        },
    },

    data() {
        return {
            // CORRECTION 1 : On convertit en Date native (.toDate()) pour le v-model
            dateRange: {
                startDate: this.startDate ? moment(this.startDate, 'YYYY-MM-DD').toDate() : moment().toDate(),
                endDate: this.endDate ? moment(this.endDate, 'YYYY-MM-DD').toDate() : moment().toDate(),
            },

            // CORRECTION 2 : Tous les ranges doivent retourner des Date natives (.toDate())
            ranges: {
                [this.$t('invoices.date_filter.today')]: [moment().startOf('day').toDate(), moment().endOf('day').toDate()],
                [this.$t('invoices.date_filter.yesterday')]: [
                    moment().subtract(1, 'days').startOf('day').toDate(),
                    moment().subtract(1, 'days').endOf('day').toDate(),
                ],
                [this.$t('invoices.date_filter.this_month')]: [
                    moment().startOf('month').toDate(),
                    moment().endOf('month').toDate(),
                ],
                [this.$t('invoices.date_filter.last_month')]: [
                    moment().subtract(1, 'month').startOf('month').toDate(),
                    moment().subtract(1, 'month').endOf('month').toDate(),
                ],
                [this.$t('invoices.date_filter.this_year')]: [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
                [this.$t('invoices.date_filter.last_year')]: [
                    moment().subtract(1, 'year').startOf('year').toDate(),
                    moment().subtract(1, 'year').endOf('year').toDate(),
                ],
            },

            localeData: {
                format: 'DD/MM/YYYY',
                separator: ' - ',
                applyLabel: this.$t('invoices.date_filter.apply'),
                cancelLabel: this.$t('invoices.date_filter.cancel'),
                weekLabel: 'W',
                daysOfWeek:
                    this.$i18n.locale === 'fr'
                        ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
                        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                monthNames:
                    this.$i18n.locale === 'fr'
                        ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
                        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                firstDay: this.$i18n.locale === 'fr' ? 1 : 0,
                customRangeLabel: this.$t('invoices.date_filter.custom'),
            },
        }
    },

    computed: {
        formattedMaxDate() {
            if (!this.maxDate) return null
            // if maxDate is a string in YYYY-MM-DD format (like 'yesterday')
            return moment(this.maxDate, 'YYYY-MM-DD').toDate()
        },
        formattedMinDate() {
            if (!this.minDate) return null
            return moment(this.minDate, 'YYYY-MM-DD').toDate()
        },
        filteredRanges() {
            let result = { ...this.ranges }

            if (this.maxDate) {
                const todayStr = moment().format('YYYY-MM-DD')
                const maxDateStr = moment(this.maxDate).format('YYYY-MM-DD')
                if (maxDateStr < todayStr) {
                    delete result[this.$t('invoices.date_filter.today')]
                }
            }

            if (this.minDate) {
                const todayStr = moment().format('YYYY-MM-DD')
                const minDateStr = moment(this.minDate).format('YYYY-MM-DD')

                // If minDate is today or later, hide past-only ranges or ranges starting in the past
                if (minDateStr >= todayStr) {
                    delete result[this.$t('invoices.date_filter.yesterday')]
                    delete result[this.$t('invoices.date_filter.last_month')]
                    delete result[this.$t('invoices.date_filter.last_year')]
                    delete result[this.$t('invoices.date_filter.this_month')]
                    delete result[this.$t('invoices.date_filter.this_year')]
                }
            }

            return result
        },
    },

    watch: {
        startDate(val) {
            if (val) {
                this.dateRange.startDate = moment(val, 'YYYY-MM-DD').toDate()
            }
        },
        endDate(val) {
            if (val) {
                this.dateRange.endDate = moment(val, 'YYYY-MM-DD').toDate()
            }
        },
    },

    methods: {
        handleDateChange(values) {
            let start = moment(values.startDate)
            let end = moment(values.endDate)

            if (!start.isValid() || !end.isValid()) {
                console.error('❌ Date invalide reçue :', values)
                this.$toast.error(this.$t('invoices.date_filter.select_period'))
                return
            }
            const formattedStart = start.format('YYYY-MM-DD')
            const formattedEnd = end.format('YYYY-MM-DD')

            console.log('📅 Plage de dates changée :', { formattedStart, formattedEnd })

            this.$emit('update:startDate', formattedStart)
            this.$emit('update:endDate', formattedEnd)
            this.$emit('change', { start: formattedStart, end: formattedEnd })
        },

        formatDateRange(startDate, endDate) {
            if (!startDate || !endDate) return this.$t('invoices.date_filter.select_period')

            const start = moment(startDate).format('YYYY-MM-DD')
            const end = moment(endDate).format('YYYY-MM-DD')
            const today = moment().format('YYYY-MM-DD')
            const yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD')

            if (start === end) {
                if (start === today) return this.$t('invoices.date_filter.today')
                if (start === yesterday) return this.$t('invoices.date_filter.yesterday')
            }

            const format = this.$i18n.locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
            return `${moment(startDate).format(format)} - ${moment(endDate).format(format)}`
        },
    },
}
</script>
<style scoped>
:deep(.vue-daterange-picker) {
    width: auto !important;
    display: block;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    position: relative;
}

:deep(.reportrange-text) {
    background: transparent;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    font-size: 0.875rem;
    color: #374151;
    outline: none !important;
    cursor: pointer;
}

/* Ensure the dropdown calendar appears above everything */
:deep(.daterangepicker) {
    z-index: 9999 !important;
}

/* Ensure the trigger area is clickable */
:deep(.form-control) {
    pointer-events: auto;
    cursor: pointer;
}
</style>
